import {
  Avatar,
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import React from "react";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import LanguageIcon from "@mui/icons-material/Language";
import Search from "components/Atoms/Input/Search/Search";
import { useThemePalette } from "common/hooks/theme_palette";
import Notifications from "components/Molecules/Notifications/Notifications";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "components/Molecules/Models/ConfirmationModal";
import { useState } from "react";
import { logoutRequest } from "services/modules/authenticate";
import setAuthToken from "services/modules/utils/handel_api";
import { useTranslation } from "react-i18next";
import MenuIcon from "@mui/icons-material/Menu";

const Header = ({ currentUserProfile, title, notificationUnseenCount, onMenuClick }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { grayBorder, primary } = useThemePalette();
  const isStore = localStorage?.getItem("user_type") == "Store";

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const dispatch = useDispatch();

  const handleLogout = () => {
    handleClose();
    navigate("/login");
    dispatch(logoutRequest({}));
    localStorage.clear();
    localStorage.removeItem("token");
    localStorage.removeItem("Messages_node");
    localStorage.removeItem("chatId");
    localStorage.removeItem("user_id");
    localStorage.removeItem("user_type");
    localStorage.removeItem("account_type");
    window.dispatchEvent(new Event("storage"));
    setAuthToken("");
  };

  const handleViewProfile = () => {
    handleClose();
    if (isStore) {
      navigate(`/vendors?type=details&id=${currentUserProfile?.id}`);
    } else {
      navigate(`/admin-profile?type=details&id=${currentUserProfile?.id}`);
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(newLang);
    document.body.dir = newLang === "ar" ? "rtl" : "ltr";
    document.documentElement.setAttribute(
      "dir",
      newLang === "ar" ? "rtl" : "ltr",
    );
  };

  const [openLogoutModal, setOpenLogoutModal] = useState(false);

  return (
    <>
      <ConfirmationModal
        open={openLogoutModal}
        setOpen={setOpenLogoutModal}
        handleConfirmation={handleLogout}
        handleCancellation={() => setOpenLogoutModal(false)}
        text={[t("header.logout_confirmation")]}
        confirmBtnTitle={t("header.logout")}
      />
      <Stack
        bgcolor="white"
        p={2}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        fontWeight="500"
        sx={{ boxShadow: "0px 2px 1px rgba(0, 0, 0, 0.1)" }}
      >
        {/* -------------- TITLE -------------- */}
        <Stack direction="row" alignItems="center" gap={1}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onMenuClick}
            sx={{ display: { lg: "none" }, mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            gutterBottom
            component="div"
            fontSize="20px"
            fontWeight="500"
            sx={{ m: 0 }}
          >
            {title}
          </Typography>
        </Stack>
        {/* -------------- SEARCH & NOTIFICATION & USER INFO -------------- */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          sx={{
            width: { xs: "auto", md: "75%" },
            flexGrow: 1,
            justifyContent: "flex-end",
          }}
        >
          {/* -------------- SEARCH -------------- */}
          <Box sx={{ display: { xs: "none", md: "block" }, flexGrow: 1, maxWidth: "400px" }}>
            <Search placeholder={t("header.search_orders_placeholder")} orange />
          </Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            {/* -------------- LANGUAGE TOGGLE -------------- */}
            <Button
              onClick={toggleLanguage}
              variant="outlined"
              startIcon={<LanguageIcon />}
              sx={{
                color: primary,
                borderColor: primary,
                fontWeight: "700",
                fontSize: "13px",
                height: "34px",
                minWidth: "78px",
                px: 1.5,
                borderRadius: "999px",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  borderColor: primary,
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                  boxShadow: "none",
                },
              }}
            >
              {i18n.language === "en"
                ? t("common.lang_ar_short")
                : t("common.lang_en_short")}
            </Button>
            {/* -------------- NOTIFICATION  -------------- */}
            <Notifications notificationUnseenCount={notificationUnseenCount} />
            {/* --------------  USER INFO  -------------- */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              sx={{ borderInlineStart: `1px solid ${grayBorder}`, px: { xs: 1, sm: 3 } }}
            >
              {/* -------------- AVATAR -------------- */}
              <Avatar
                alt={currentUserProfile?.name || t("common.admin")}
                src={
                  currentUserProfile?.logo ||
                  currentUserProfile?.avatar ||
                  "/static/images/avatar/1.jpg"
                }
                sx={{ borderRadius: "50%" }}
              />
              {/* -------------- USERNAME & ROLE -------------- */}
              <Stack gap={0.5} sx={{ display: { xs: "none", sm: "flex" } }}>
                <Typography
                  fontFamily="Cairo"
                  fontWeight="700"
                  fontSize="16px"
                  sx={{ lineHeight: 1 }}
                >
                  {currentUserProfile?.name || t("common.admin")}
                </Typography>
                <Typography
                  fontFamily="Cairo"
                  fontWeight="500"
                  fontSize="12px"
                  color="#8F9BB3"
                  sx={{ lineHeight: 1 }}
                >
                  {currentUserProfile?.role?.name || t("common.admin")}
                </Typography>
              </Stack>
              {/* -------------- DROP DOWN -------------- */}
              <Box>
                <Box
                  sx={{ display: "flex" }}
                  id="demo-positioned-button"
                  aria-controls={open ? "demo-positioned-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  onClick={handleClick}
                >
                  <ArrowForwardIosIcon
                    sx={{ transform: "rotate(90deg)" }}
                    fontSize="small"
                  />
                </Box>
                <Menu
                  id="demo-positioned-menu"
                  aria-labelledby="demo-positioned-button"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                >
                  <MenuItem onClick={handleViewProfile}>
                    {t("header.view_profile")}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setOpenLogoutModal(true);
                    }}
                  >
                    {t("header.logout")}
                  </MenuItem>
                </Menu>
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};

export default Header;
